"use client";

import { ArrowRight, Link as LucideLink, Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CultivatingCommunity() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const {
    ref: paraRef,
    isVisible: paraVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineWords = [{
    text: "Where",
    italic: false
  }, {
    text: "nature",
    italic: false
  }, {
    text: "and",
    italic: false
  }, {
    text: "community",
    italic: true
  }, {
    text: "grow",
    italic: false
  }, {
    text: "together.",
    italic: false
  }];
  const stats = [{
    value: "12+",
    label: "Years Cultivating"
  }, {
    value: "450+",
    label: "Active Members"
  }, {
    value: "100%",
    label: "Organic Harvests"
  }];
  return <section data-section-id="913" id="cultivating-community" className="relative overflow-hidden bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <Leaf className="absolute top-12 left-8 size-16 text-secondary opacity-[0.05] -rotate-12" strokeWidth={1.5} />
        <Leaf className="absolute top-1/3 right-12 size-20 text-primary opacity-[0.05] rotate-12" strokeWidth={1.5} />
        <Leaf className="absolute bottom-20 left-1/4 size-14 text-secondary opacity-[0.05] rotate-45" strokeWidth={1.5} />
        <Leaf className="absolute bottom-32 right-1/3 size-12 text-primary opacity-[0.05] -rotate-45" strokeWidth={1.5} />
        {}
        <div className="absolute top-1/4 left-0 size-[500px] rounded-lg bg-primary/10 blur-3xl" style={{
        borderRadius: "60% 40% 50% 50% / 50% 60% 40% 50%"
      }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-12 lg:items-center">
          {}
          <div className="lg:col-span-7">
            <span ref={eyebrowRef} className={`inline-block font-default text-xs font-semibold uppercase tracking-[0.2em] text-secondary transition-all duration-700 ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
              Neighborhood Gardening Club
            </span>

            <h1 ref={headlineRef} className="mt-6 font-default text-5xl font-medium leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
              {headlineWords.map((word, i) => <span key={i} data-index={i} className={`inline-block transition-all duration-700 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${word.italic ? "italic text-primary font-light" : ""}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {word.text}
                  {i < headlineWords.length - 1 && "\u00A0"}
                </span>)}
            </h1>

            <p ref={paraRef} className={`mt-8 max-w-[480px] font-default text-lg leading-relaxed text-muted-foreground transition-all duration-700 delay-300 ${paraVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              Eco Grow is your local sanctuary for sustainable living. Join our neighborhood club to share seeds, learn organic farming techniques, and cultivate fresh produce in our shared community plots.
            </p>

            <div ref={ctaRef} className={`mt-10 flex flex-col gap-4 sm:flex-row sm:items-center transition-all duration-700 delay-500 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild size="lg" className="h-12 bg-primary px-8 text-primary-foreground shadow-md hover:bg-primary/90">
                <Link to="/JoinUsPage">Download Planting Guide</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 border-primary/30 bg-transparent px-8 text-primary hover:bg-primary/5 hover:text-primary">
                <Link to="/TheGardenPage">
                  Explore Our Plots
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {}
            <div ref={statsRef} className={`mt-14 grid grid-cols-3 gap-6 border-t border-border pt-8 transition-all duration-700 delay-700 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {stats.map((stat, i) => <div key={i} data-index={i} className={i < stats.length - 1 ? "border-r border-border pr-4" : "pr-0"}>
                  <div className="font-default text-xs uppercase tracking-wider text-muted-foreground">
                    {stat.label}
                  </div>
                  <div className="mt-2 font-default text-3xl font-medium text-primary tabular-nums sm:text-4xl">
                    {stat.value}
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative lg:col-span-5 transition-all duration-1000 ${imageVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="relative overflow-hidden shadow-lg" style={{
            borderRadius: "12px"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/community_garden_harvest.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/community_garden_harvest.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/community_garden_harvest.png-wvc-sizes" alt="Fresh seasonal harvest from our community garden" loading="lazy" decoding="async" className="size-full object-cover aspect-[4/5]" />
            </div>

            {}
            <div className="absolute -bottom-6 -left-4 max-w-[260px] rounded-lg bg-card p-4 shadow-lg sm:-left-8 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Leaf className="size-5 text-primary" strokeWidth={2} />
                </div>
                <p className="font-default text-sm leading-snug text-card-foreground">
                  Growing together since 2012
                </p>
              </div>
            </div>

            {}
            <div className="absolute -right-8 -top-8 -z-10 size-32 bg-primary/20 blur-2xl" style={{
            borderRadius: "60% 40% 50% 50%"
          }} aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>;
}